import React, { HTMLAttributes } from 'react'

import styles from './Flex.module.scss'

export type FlexProps<T extends keyof JSX.IntrinsicElements = 'div'> = {
	/** Render as a different HTML element, e.g. 'ul' or 'section' */
	as?: T
} & {
	inline?: boolean
	row?: boolean
	rowReverse?: boolean
	column?: boolean
	columnReverse?: boolean
	nowrap?: boolean
	wrap?: boolean
	wrapReverse?: boolean
	justifyStart?: boolean
	justifyEnd?: boolean
	justifyCenter?: boolean
	justifybetween?: boolean
	justifyAround?: boolean
	contentStart?: boolean
	contentEnd?: boolean
	contentCenter?: boolean
	contentSpaceBetween?: boolean
	contentSpaceAround?: boolean
	contentStretch?: boolean
	alignStart?: boolean
	alignEnd?: boolean
	alignCenter?: boolean
	alignBaseline?: boolean
	alignStretch?: boolean
	full?: boolean
	center?: boolean
	gap?: number
	columnGap?: number
	rowGap?: number // TODO В доке указать, что здесь пиксели
	className?: string
} & HTMLAttributes<any>

// A lightweight Flex component using SCSS module and inline styles for gaps
export default function Flex<T extends keyof JSX.IntrinsicElements = 'div'>({
	children,
	className,
	gap,
	columnGap,
	rowGap,
	as,
	...rest
}: FlexProps<T>) {
	const Component: any = as || 'div'

	const cls = [styles.flex]

	if (className) cls.push(className)

	// map boolean props to utility classes (preserve old behavior)
	const booleanClassMap: Array<[keyof FlexProps, string]> = [
		['inline', 'inline'],
		['row', 'row'],
		['rowReverse', 'rowReverse'],
		['column', 'column'],
		['columnReverse', 'columnReverse'],
		['nowrap', 'nowrap'],
		['wrap', 'wrap'],
		['wrapReverse', 'wrapReverse'],
		['justifyStart', 'justifyStart'],
		['justifyEnd', 'justifyEnd'],
		['justifyCenter', 'justifyCenter'],
		['justifybetween', 'justifyBetween'],
		['justifyAround', 'justifyAround'],
		['contentStart', 'contentStart'],
		['contentEnd', 'contentEnd'],
		['contentCenter', 'contentCenter'],
		['contentSpaceBetween', 'contentSpaceBetween'],
		['contentSpaceAround', 'contentSpaceAround'],
		['contentStretch', 'contentStretch'],
		['alignStart', 'alignStart'],
		['alignEnd', 'alignEnd'],
		['alignCenter', 'alignCenter'],
		['alignBaseline', 'alignBaseline'],
		['alignStretch', 'alignStretch'],
		['full', 'full'],
		['center', 'center']
	]

	for (const [propKey, classKey] of booleanClassMap) {
		if ((rest as any)[propKey]) {
			// map camelCase class names to module keys (remove ambiguity)
			const moduleClass =
				(styles as any)[classKey] || (styles as any)[classKey.replace(/[A-Z]/g, m => '-' + m.toLowerCase())]
			if (moduleClass) cls.push(moduleClass)
		}
	}

	// style variables for gaps mimic previous behavior
	const style: React.CSSProperties = {}
	if (typeof columnGap === 'number') {
		// @ts-ignore
		style['--gap-x' as any] = `${columnGap}px`
	}
	if (typeof rowGap === 'number') {
		// @ts-ignore
		style['--gap-y' as any] = `${rowGap}px`
	}
	if (typeof gap === 'number') {
		// @ts-ignore
		style['--gap' as any] = `${gap}px`
	}

	const {
		column: _column,
		center: _center,
		full: _full,
		alignCenter: _alignCenter,
		wrap: _wrap,
		alignEnd: _alignEnd,
		...forDiv
	} = rest

	return (
		<Component className={cls.join(' ')} style={style} {...forDiv}>
			{children}
		</Component>
	)
}
